<template>
  <div class="case-header">
    <div class="left">
      <img
        src="@/assets/icons/i-return.png"
        style="width: 36px; height: 36px; cursor: pointer"
        @click="emit('back')"
      />
      <span>案例原文</span>
    </div>

    <div class="right">
      <a-button @click="toggleFind">
        <SearchOutlined />
        页面内搜索
      </a-button>
      <a-button
        :class="{ favorited: isFavorited }"
        @click="toggleFavorite"
        @mouseenter="starHover = true"
        @mouseleave="starHover = false"
      >
        <img :src="starIcon" class="icons" />
        {{ isFavorited ? "已收藏" : "收藏" }}
      </a-button>

      <a-button
        @click="handleDownload"
        :loading="downloading"
        @mouseenter="downloadHover = true"
        @mouseleave="downloadHover = false"
      >
        <img
          :src="
            downloadHover
              ? require('@/assets/casesearchImg/i-download-two.png')
              : require('@/assets/casesearchImg/i-download.png')
          "
          class="icons"
        />
        下载
      </a-button>
    </div>

    <!-- 页面内搜索栏 -->
    <div v-if="showFind" class="find-bar">
      <a-input
        ref="findInputRef"
        v-model:value="findKeyword"
        placeholder="输入搜索关键词"
        size="small"
        style="width: 200px"
        @press-enter="doFind"
        @input="doFind"
      >
        <template #prefix>
          <SearchOutlined />
        </template>
      </a-input>
      <span class="find-count">{{ findResultIndex }}/{{ findTotal }}</span>
      <a-button size="small" type="text" @click="findPrev">
        <LeftOutlined />
      </a-button>
      <a-button size="small" type="text" @click="findNext">
        <RightOutlined />
      </a-button>
      <a-button size="small" type="text" @click="closeFind">
        <CloseOutlined />
      </a-button>
    </div>

    <!-- 收藏夹选择弹窗 -->
    <a-modal
      v-model:open="favoriteModalVisible"
      title="选择收藏夹"
      @ok="handleFavoriteConfirm"
      :confirmLoading="favoriteConfirmLoading"
      okText="确定"
      cancelText="取消"
    >
      <a-select
        v-model:value="selectedFolderId"
        style="width: 100%"
        placeholder="请选择收藏夹"
      >
        <a-select-option
          v-for="folder in folderList"
          :key="folder.id"
          :value="folder.id"
        >
          {{ folder.name }}
        </a-select-option>
      </a-select>
    </a-modal>
  </div>
</template>
<script setup>
import { ref, computed, nextTick } from "vue";
import {
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons-vue";
import {
  BatchAddByArray,
  BatchDownloadCases,
  RemoveByCaseNumber,
} from "../../api/case/case";
import { getFolderList } from "../../api/collect/folder";
import { message } from "ant-design-vue";

const props = defineProps({
  caseNumber: {
    type: String,
    default: "",
  },
  isFavorited: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["back", "find", "favoriteChange"]);

const showFind = ref(false);
const findKeyword = ref("");
const findInputRef = ref(null);
const findTotal = ref(0);
const findResultIndex = ref(0);
const starHover = ref(false);
const downloadHover = ref(false);
const downloading = ref(false);

// 收藏弹窗
const favoriteModalVisible = ref(false);
const favoriteConfirmLoading = ref(false);
const folderList = ref([]);
const selectedFolderId = ref(undefined);

// 星标图标：已收藏或hover时用金色星，否则用灰色星
const starIcon = computed(() => {
  if (props.isFavorited || starHover.value) {
    return require("@/assets/casesearchImg/i-star2.png");
  }
  return require("@/assets/casesearchImg/i-star.png");
});

// 收藏/取消收藏
const toggleFavorite = async () => {
  if (!props.caseNumber) {
    message.warning("暂无案号，无法收藏");
    return;
  }
  if (props.isFavorited) {
    // 已收藏 → 取消收藏
    try {
      await RemoveByCaseNumber(
        props.caseNumber.replace(/[（）]/g, (m) => (m === "（" ? "(" : ")")),
      );
      message.success("取消收藏成功");
      emit("favoriteChange", false);
    } catch (err) {
      console.error("取消收藏失败", err);
      message.error("取消收藏失败");
    }
  } else {
    // 未收藏 → 弹出收藏夹选择弹窗
    try {
      const res = await getFolderList();
      folderList.value = res?.data || [];
    } catch (err) {
      console.error("获取收藏夹列表失败", err);
      folderList.value = [];
    }
    selectedFolderId.value = undefined;
    favoriteModalVisible.value = true;
  }
};

// 确定收藏
const handleFavoriteConfirm = async () => {
  if (!selectedFolderId.value) {
    message.warning("请选择一个收藏夹");
    return;
  }
  favoriteConfirmLoading.value = true;
  try {
    await BatchAddByArray({
      folderId: selectedFolderId.value,
      favoriteType: "类案检索",
      caseNumbers: [
        props.caseNumber.replace(/[（）]/g, (m) => (m === "（" ? "(" : ")")),
      ],
    });
    message.success("收藏成功");
    favoriteModalVisible.value = false;
    emit("favoriteChange", true);
  } catch (err) {
    console.error("收藏失败", err);
    message.error("收藏失败");
  } finally {
    favoriteConfirmLoading.value = false;
  }
};

// 下载案例
const handleDownload = async () => {
  if (!props.caseNumber) {
    message.warning("暂无案号，无法下载");
    return;
  }
  downloading.value = true;
  try {
    const blob = await BatchDownloadCases({ caseNumbers: [props.caseNumber] });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${props.caseNumber}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    message.success("下载成功");
  } catch (err) {
    console.error("下载失败", err);
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

const toggleFind = () => {
  showFind.value = !showFind.value;
  if (showFind.value) {
    nextTick(() => {
      findInputRef.value?.focus();
    });
  } else {
    clearHighlights();
    findKeyword.value = "";
    findTotal.value = 0;
    findResultIndex.value = 0;
  }
};

const closeFind = () => {
  showFind.value = false;
  clearHighlights();
  findKeyword.value = "";
  findTotal.value = 0;
  findResultIndex.value = 0;
};

// 清除所有高亮
const clearHighlights = () => {
  document.querySelectorAll(".find-highlight").forEach((el) => {
    const parent = el.parentNode;
    parent.replaceChild(document.createTextNode(el.textContent), el);
    parent.normalize();
  });
  // 移除临时标记
  document.querySelectorAll(".find-current").forEach((el) => {
    el.classList.remove("find-current");
  });
};

// 执行查找
const doFind = () => {
  clearHighlights();
  findTotal.value = 0;
  findResultIndex.value = 0;

  const keyword = findKeyword.value.trim();
  if (!keyword) return;

  const content =
    document.querySelector(".content-wrap") ||
    document.querySelector(".doc-card");
  if (!content) return;

  // 遍历文本节点进行高亮
  const treeWalker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (treeWalker.nextNode()) {
    textNodes.push(treeWalker.currentNode);
  }

  let count = 0;
  textNodes.forEach((node) => {
    const text = node.textContent;
    if (!text || !text.toLowerCase().includes(keyword.toLowerCase())) return;

    const parent = node.parentNode;
    if (
      parent &&
      (parent.classList?.contains("find-highlight") ||
        parent.closest(".find-highlight"))
    )
      return;

    const parts = text.split(
      new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"),
    );
    if (parts.length <= 1) return;

    const fragment = document.createDocumentFragment();
    parts.forEach((part) => {
      if (part.toLowerCase() === keyword.toLowerCase()) {
        const mark = document.createElement("mark");
        mark.className = "find-highlight";
        mark.textContent = part;
        fragment.appendChild(mark);
        count++;
      } else if (part) {
        fragment.appendChild(document.createTextNode(part));
      }
    });
    parent.replaceChild(fragment, node);
  });

  findTotal.value = count;
  if (count > 0) {
    const allHighlights = document.querySelectorAll(".find-highlight");
    if (allHighlights.length > 0) {
      findResultIndex.value = 1;
      allHighlights[0].classList.add("find-current");
      allHighlights[0].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
};

// 查找下一个
const findNext = () => {
  const all = document.querySelectorAll(".find-highlight");
  if (all.length === 0) return;
  const current = document.querySelector(".find-current");
  let idx = 0;
  if (current) {
    current.classList.remove("find-current");
    idx = Array.from(all).indexOf(current) + 1;
    if (idx >= all.length) idx = 0;
  }
  all[idx].classList.add("find-current");
  all[idx].scrollIntoView({ behavior: "smooth", block: "center" });
  findResultIndex.value = idx + 1;
};

// 查找上一个
const findPrev = () => {
  const all = document.querySelectorAll(".find-highlight");
  if (all.length === 0) return;
  const current = document.querySelector(".find-current");
  let idx = all.length - 1;
  if (current) {
    current.classList.remove("find-current");
    idx = Array.from(all).indexOf(current) - 1;
    if (idx < 0) idx = all.length - 1;
  }
  all[idx].classList.add("find-current");
  all[idx].scrollIntoView({ behavior: "smooth", block: "center" });
  findResultIndex.value = idx + 1;
};
</script>
<style scoped lang="less">
.case-header {
  display: flex;

  justify-content: space-between;

  align-items: center;

  height: 58px;

  background: #fff;

  border: 1px solid #e6ecf5;

  border-radius: 10px;

  padding: 0 16px;

  margin-bottom: 16px;

  position: relative;
}

.left {
  display: flex;

  align-items: center;

  flex: 1;

  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  color: #12213f;
  line-height: 22px;
}

.back-btn {
  width: 36px;

  height: 36px;
  background-color: #1768ff;
  color: #fff;
  font-weight: 600;
}

.search-input {
  height: 40px;
  flex: 1;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #dfe6f2;
  font-weight: 400;
  font-size: 13px;
  color: #3e4d67;
}

.right {
  display: flex;
  align-items: center;
  gap: 16px;
  font-weight: 600;
  font-size: 13px;
  color: #31425e;
}
.right .icons {
  width: 20px;
  height: 20px;
  margin-right: 4px;
}
.right :deep(button) {
  display: inline-flex;
  align-items: center;
}
/* 已收藏态：边框和文字用主题色（hover/focus 也钉住，防 antd 运行时样式覆盖） */
.right :deep(button.favorited) {
  color: #1768ff;
  border-color: #1768ff;

  &:hover,
  &:focus {
    color: #1768ff;
    border-color: #1768ff;
  }
}

/* 查找栏 */
.find-bar {
  position: absolute;
  top: 100%;
  right: 16px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e6ecf5;
  border-radius: 10px;
  padding: 8px 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 100;
}
.find-count {
  font-size: 12px;
  color: #86909c;
  white-space: nowrap;
  min-width: 30px;
  text-align: center;
}
</style>

<style>
/* 全局高亮样式（非 scoped 确保穿透 shadow DOM） */
.find-highlight {
  background: #ffe066;
  color: #1f2329;
  padding: 0 1px;
  border-radius: 2px;
}
.find-current {
  background: #ffb800;
  color: #1f2329;
  padding: 0 1px;
  border-radius: 2px;
}
</style>
